"use client"

import type React from "react"
import { useRef, useState } from "react"
import { useRouter } from "@/lib/navigation"

import { useAuth } from "@/contexts/auth-context"
import { apiRequest } from "@/lib/api-config"
import { useToast } from "@/hooks/use-toast"
import { TurnstileWidget } from "@/components/turnstile-widget"
import { cn } from "@/lib/utils"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

type Mode = "user" | "admin" | "register"

type FieldErrors = Record<string, string | undefined>

// 后端校验的 CDK 格式：32 位纯字母
const CDK_PATTERN = /^[A-Za-z]{32}$/

const TURNSTILE_ACTION: Record<
  Mode,
  "user_login" | "admin_login" | "user_register"
> = {
  user: "user_login",
  admin: "admin_login",
  register: "user_register",
}

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const router = useRouter()
  const { login } = useAuth()
  const { toast } = useToast()

  const [mode, setMode] = useState<Mode>("user")
  /** 正在等待人机验证的模式；null 表示弹窗关闭 */
  const [pending, setPending] = useState<Mode | null>(null)
  const pendingRef = useRef<Mode | null>(null)
  const [loading, setLoading] = useState(false)
  const submitting = useRef(false)

  const [userForm, setUserForm] = useState({ account: "", password: "" })
  const [adminForm, setAdminForm] = useState({ username: "", password: "" })
  const [registerForm, setRegisterForm] = useState({
    account: "",
    password: "",
    cdk: "",
    server: "0",
  })

  const [errors, setErrors] = useState<FieldErrors>({})
  /** 每次提交结束后自增，强制 Turnstile 重新验证 */
  const [resetKey, setResetKey] = useState(0)

  const startPending = (target: Mode) => {
    pendingRef.current = target
    setPending(target)
  }

  const clearPending = () => {
    pendingRef.current = null
    setPending(null)
    setResetKey((k) => k + 1)
  }

  // 切换 Tab 时收起弹窗并清空字段错误
  const handleModeChange = (value: string) => {
    setMode(value as Mode)
    setErrors({})
    if (!loading) clearPending()
  }

  /** 字段校验：返回空对象表示通过 */
  const validate = (target: Mode): FieldErrors => {
    const next: FieldErrors = {}
    if (target === "user") {
      if (!userForm.account.trim()) next.account = "请输入账号"
      if (!userForm.password) next.password = "请输入密码"
      return next
    }
    if (target === "admin") {
      if (!adminForm.username.trim()) next.username = "请输入用户名"
      if (!adminForm.password) next.password = "请输入密码"
      return next
    }
    const cdk = registerForm.cdk.trim()
    if (!registerForm.account.trim()) next.account = "请输入登录账号"
    if (!registerForm.password) next.password = "请输入登录密码"
    if (!cdk) next.cdk = "请输入 CDK 激活码"
    else if (!CDK_PATTERN.test(cdk)) next.cdk = "CDK 格式错误，应为 32 位字母"
    return next
  }

  /** 第一阶段：点击提交 → 校验字段 → 通过则弹出人机验证 */
  const requestVerify = (target: Mode) => (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting.current || loading) return
    const next = validate(target)
    setErrors(next)
    if (Object.keys(next).length > 0) return
    startPending(target)
  }

  /** 第二阶段：拿到 Turnstile token → 真正提交 */
  const handleToken = async (token: string | null) => {
    const target = pendingRef.current
    // Turnstile 在卸载时会回调 null，这里必须忽略
    if (!token || !target || submitting.current) return
    submitting.current = true
    setLoading(true)
    try {
      if (target === "user") await submitUser(token)
      else if (target === "admin") await submitAdmin(token)
      else await submitRegister(token)
    } finally {
      submitting.current = false
      setLoading(false)
      clearPending()
    }
  }

  const submitUser = async (token: string) => {
    const result = (await apiRequest("/userLogin", {
      method: "POST",
      body: JSON.stringify({ ...userForm, turnstileToken: token }),
    })) as { code: number; data: { token: string }; msg?: string }

    if (result.code === 200) {
      login(result.data.token, "user")
      toast({
        variant: "success",
        title: "登录成功",
        description: "欢迎回来！正在跳转到用户面板...",
        duration: 3000,
        className: "login-success-toast",
      })
      router.push("/user/dashboard")
      return
    }
    toast({
      variant: "destructive",
      title: "登录失败",
      description: result.msg || "用户名或密码错误",
    })
  }

  const submitAdmin = async (token: string) => {
    const result = (await apiRequest("/adminLogin", {
      method: "POST",
      body: JSON.stringify({ ...adminForm, turnstileToken: token }),
    })) as { code: number; data: { token: string }; msg?: string }

    if (result.code === 200) {
      login(result.data.token, "admin")
      toast({
        variant: "success",
        title: "管理员登录成功",
        description: "欢迎回来！正在跳转到管理员面板...",
        duration: 3000,
        className: "login-success-toast",
      })
      router.push("/admin/dashboard")
      return
    }
    toast({
      variant: "destructive",
      title: "登录失败",
      description: result.msg || "用户名或密码错误",
    })
  }

  const submitRegister = async (token: string) => {
    const cdk = registerForm.cdk.trim()
    const account = registerForm.account.trim()

    const result = (await apiRequest("/createUserByCDK", {
      method: "POST",
      body: JSON.stringify({
        cdk,
        username: account,
        account,
        password: registerForm.password,
        server: Number.parseInt(registerForm.server),
        turnstileToken: token,
      }),
    })) as { code: number; data?: unknown; msg?: string }

    if (result.code === 200) {
      toast({
        variant: "success",
        title: "注册成功",
        description: "账号已创建，请完成人机验证后登录",
      })
      setRegisterForm({ account: "", password: "", cdk: "", server: "0" })
      setMode("user")
      return
    }
    // 业务错误（CDK 无效 / 账号已存在）就地显示在 CDK 字段下方
    setErrors((prev) => ({ ...prev, cdk: result.msg || "CDK 无效或账号已存在" }))
  }

  /** 人机验证弹窗：仅在 pending 有值时挂载 Turnstile，脚本按需加载 */
  const verifyDialog = (
    <Dialog
      open={pending !== null}
      onOpenChange={(open) => {
        // 提交过程中不允许关闭，避免中断请求
        if (!open && !loading) clearPending()
      }}
    >
      <DialogContent className="max-w-[340px] gap-3 p-4">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-center text-base">人机验证</DialogTitle>
          <DialogDescription className="text-center text-xs">
            {loading ? "验证已通过，正在提交..." : "请完成验证以继续"}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center">
          {pending && (
            <TurnstileWidget
              action={TURNSTILE_ACTION[pending]}
              onTokenChange={handleToken}
              resetKey={resetKey}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      {verifyDialog}

      <Card>
        <CardHeader>
          <CardTitle>登录你的账号</CardTitle>
          <CardDescription>
            选择身份并输入账号信息，或使用 CDK 激活码注册新账号
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={mode} onValueChange={handleModeChange}>
            <TabsList className="mb-2 grid w-full grid-cols-3">
              <TabsTrigger value="user">用户</TabsTrigger>
              <TabsTrigger value="admin">管理员</TabsTrigger>
              <TabsTrigger value="register">CDK 注册</TabsTrigger>
            </TabsList>

            {/* ============ 用户登录 ============ */}
            <TabsContent value="user">
              <form onSubmit={requestVerify("user")} noValidate>
                <FieldGroup>
                  <Field data-invalid={!!errors.account}>
                    <FieldLabel htmlFor="user-account">账号</FieldLabel>
                    <Input
                      id="user-account"
                      name="account"
                      autoComplete="username"
                      placeholder="请输入账号"
                      value={userForm.account}
                      aria-invalid={!!errors.account}
                      onChange={(e) =>
                        setUserForm({ ...userForm, account: e.target.value })
                      }
                    />
                    <FieldError>{errors.account}</FieldError>
                  </Field>

                  <Field data-invalid={!!errors.password}>
                    <FieldLabel htmlFor="user-password">密码</FieldLabel>
                    <Input
                      id="user-password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={userForm.password}
                      aria-invalid={!!errors.password}
                      onChange={(e) =>
                        setUserForm({ ...userForm, password: e.target.value })
                      }
                    />
                    <FieldError>{errors.password}</FieldError>
                  </Field>

                  <Field>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "登录中..." : "登录"}
                    </Button>
                    <FieldDescription className="text-center">
                      还没有账号？切换到「CDK 注册」用激活码创建
                    </FieldDescription>
                  </Field>
                </FieldGroup>
              </form>
            </TabsContent>

            {/* ============ 管理员登录 ============ */}
            <TabsContent value="admin">
              <form onSubmit={requestVerify("admin")} noValidate>
                <FieldGroup>
                  <Field data-invalid={!!errors.username}>
                    <FieldLabel htmlFor="admin-username">用户名</FieldLabel>
                    <Input
                      id="admin-username"
                      name="username"
                      autoComplete="username"
                      placeholder="请输入管理员用户名"
                      value={adminForm.username}
                      aria-invalid={!!errors.username}
                      onChange={(e) =>
                        setAdminForm({ ...adminForm, username: e.target.value })
                      }
                    />
                    <FieldError>{errors.username}</FieldError>
                  </Field>

                  <Field data-invalid={!!errors.password}>
                    <FieldLabel htmlFor="admin-password">密码</FieldLabel>
                    <Input
                      id="admin-password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={adminForm.password}
                      aria-invalid={!!errors.password}
                      onChange={(e) =>
                        setAdminForm({ ...adminForm, password: e.target.value })
                      }
                    />
                    <FieldError>{errors.password}</FieldError>
                  </Field>

                  <Field>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "登录中..." : "管理员登录"}
                    </Button>
                  </Field>
                </FieldGroup>
              </form>
            </TabsContent>

            {/* ============ CDK 注册 ============ */}
            <TabsContent value="register">
              <form onSubmit={requestVerify("register")} noValidate>
                <FieldGroup>
                  <Field data-invalid={!!errors.account}>
                    <FieldLabel htmlFor="register-account">设置账号</FieldLabel>
                    <Input
                      id="register-account"
                      name="account"
                      autoComplete="username"
                      placeholder="请设置登录账号"
                      value={registerForm.account}
                      aria-invalid={!!errors.account}
                      onChange={(e) =>
                        setRegisterForm({
                          ...registerForm,
                          account: e.target.value,
                        })
                      }
                    />
                    <FieldError>{errors.account}</FieldError>
                  </Field>

                  <Field data-invalid={!!errors.password}>
                    <FieldLabel htmlFor="register-password">设置密码</FieldLabel>
                    <Input
                      id="register-password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="请设置登录密码"
                      value={registerForm.password}
                      aria-invalid={!!errors.password}
                      onChange={(e) =>
                        setRegisterForm({
                          ...registerForm,
                          password: e.target.value,
                        })
                      }
                    />
                    <FieldError>{errors.password}</FieldError>
                  </Field>

                  <Field data-invalid={!!errors.cdk}>
                    <FieldLabel htmlFor="register-cdk">CDK 激活码</FieldLabel>
                    <Input
                      id="register-cdk"
                      name="cdk"
                      maxLength={32}
                      placeholder="请输入 32 位字母激活码"
                      value={registerForm.cdk}
                      aria-invalid={!!errors.cdk}
                      onChange={(e) =>
                        setRegisterForm({
                          ...registerForm,
                          cdk: e.target.value.replace(/\s/g, ""),
                        })
                      }
                    />
                    <FieldError>{errors.cdk}</FieldError>
                  </Field>

                  <Field>
                    <FieldLabel>选择区服</FieldLabel>
                    <RadioGroup
                      value={registerForm.server}
                      onValueChange={(v) =>
                        setRegisterForm({ ...registerForm, server: v })
                      }
                      className="flex flex-wrap gap-6"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="0" id="server-official" />
                        <Label htmlFor="server-official">官服</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="1" id="server-b" />
                        <Label htmlFor="server-b">B 服</Label>
                      </div>
                    </RadioGroup>
                  </Field>

                  <Field>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "注册中..." : "注册并登录"}
                    </Button>
                    <FieldDescription className="text-center">
                      已有账号？切换到「用户」直接登录
                    </FieldDescription>
                  </Field>
                </FieldGroup>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
